import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { motion } from "framer-motion";

import { useTheme } from "@mui/material/styles";
import DashboardContentLayout from "../dashboardLayout";
import useResponsive from "../../hooks/useResponsive";

const Header: React.FC<{
  children: React.ReactNode;
  scrollContainerId?: string;
}> = ({ children, scrollContainerId }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const currentScrollY = scrollContainerRef.current.scrollTop;

    if (currentScrollY > lastScrollY) {
      // Scrolling down
      setIsVisible(false);
    } else {
      // Scrolling up
      setIsVisible(true);
    }

    setLastScrollY(currentScrollY);
  };

  useEffect(() => {
    if (scrollContainerId) {
      scrollContainerRef.current = document.getElementById(scrollContainerId);
    } else {
      scrollContainerRef.current = document.documentElement; // Fallback to window (default behavior)
    }

    const scrollElement = scrollContainerRef.current;

    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollContainerId, lastScrollY]);

  return (
    <motion.div
      initial={{ y: -150 }} // Initial position: above the viewport
      animate={{ y: isVisible ? 0 : -150 }} // Animate to visible or hidden
      transition={{ duration: 0.3, ease: "easeInOut" }} // Smooth transition
      style={{
        position: "fixed",
        zIndex: 10,
        height: 75,
        marginTop: "1px",
        width: "100%",
        overflowX: "hidden",
        border: "1px solid rgba(255,255,255,0.7)",
        backdropFilter: "blur(20px)",
        background:
          "radial-gradient(97.57% 210.75% at 0.9% 2.98%, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%)",
      }}
    >
      <DashboardContentLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            px: 2,
            height: 70,
          }}
        >
          {children}
          <Stack sx={{ gap: "10px", ml: "auto" }} alignItems={"center"}></Stack>
        </Box>
      </DashboardContentLayout>
    </motion.div>
  );
};

export default Header;
